<template>
  <BannerHeroBlank :segment="segment" :dark="dark || isEnterprise">
    <div
      class="md:container flex flex-col items-center justify-between gap-8 md:flex-row md:px-10"
    >
      <div class="w-full">
        <div
          class="tag max-md:text-center"
          :class="dark || isEnterprise ? '!text-green-300' : '!text-lemon-200'"
        >
          {{ tagline }}
        </div>
        <h2 class="mb-6 text-white max-md:text-center">{{ heading }}</h2>
        <div v-if="description">
          <template v-if="Array.isArray(description)">
            <p
              v-for="(item, idx) in description"
              :key="idx"
              class="max-w-2xl text-white last:mb-8 max-md:text-center"
              v-html="item"
            ></p>
          </template>
          <p
            v-else
            class="!mb-8 max-w-2xl text-white max-md:text-center"
            v-html="description"
          ></p>
        </div>

        <div
          v-if="buttonPrimary || buttonSecondary"
          class="flex flex-col gap-6 sm:flex-row md:flex-col lg:flex-row"
        >
          <component
            :is="isEnterprise ? BtnPrimary : BtnHero"
            v-if="buttonPrimary"
            size="large"
            :arrow="buttonPrimary.offSite ? 'upRight' : 'right'"
            :label="buttonPrimary.label"
            :url="buttonPrimary.url"
            :url-new-window="buttonPrimary.urlNewWindow"
            :on-dark="dark || isEnterprise"
            class="w-full md:w-fit md:min-w-60"
            @click="$emit('click-primary', $event)"
          />
          <BtnSecondary
            v-if="buttonSecondary"
            size="large"
            :arrow="buttonSecondary.offSite ? 'upRight' : 'right'"
            :label="buttonSecondary.label"
            :url="buttonSecondary.url"
            :url-new-window="buttonSecondary.urlNewWindow"
            :on-dark="true"
            class="w-full md:w-fit md:min-w-60"
            @click="$emit('click-secondary', $event)"
          />
        </div>
        <TextMicrocopy
          v-if="microcopy"
          arrow-icon="IconArrowLeftUp"
          :text="microcopy"
          :on-dark="true"
          class="ml-4 mt-4"
        />
        <p
          v-if="disclaimer"
          class="mt-8 text-sm text-grey-50"
          v-html="disclaimer"
        ></p>
      </div>
      <div
        v-if="img && img.skewedImg"
        class="relative order-first m-4 aspect-square size-full max-h-[18rem] max-w-[18rem] shrink-0 rotate-1 overflow-hidden rounded-3xl shadow-md [backface-visibility:hidden] md:order-last lg:max-h-[22rem] lg:max-w-[22rem]"
        :class="{
          'before:pointer-events-none before:absolute before:inset-0 before:block before:size-full before:bg-gradient-to-b before:from-jag-950/0 before:via-jag-950/0 before:to-jag-950/80 before:to-80% before:mix-blend-multiply':
            hasGradient,
        }"
      >
        <img
          :src="img.src"
          :alt="img.alt ?? ''"
          :width="img.width"
          :height="img.height"
          class="size-full object-cover"
          :class="img.customClass"
        />

        <div
          v-if="img.label"
          class="absolute inset-0 flex items-end justify-center px-4 py-8 md:px-12 md:py-8"
        >
          <span
            class="nimpkish text-center text-2xl lg:text-4xl"
            :class="[
              !isEnterprise || img.lemon ? 'text-lemon-200' : 'text-white',
              img.textSize,
            ]"
            v-html="img.label"
          ></span>
        </div>
      </div>
      <img
        v-else-if="img && !img.skewedImg"
        :src="img.src"
        :alt="img.alt"
        class="order-first m-4 w-full max-w-80 md:order-last lg:max-w-96"
        :class="[{ 'rotate-1': img.rotate }, img.customClass]"
      />
      <slot name="img"></slot>
    </div>
  </BannerHeroBlank>
</template>

<script setup lang="ts">
import type { Segment } from "@/types";

import BtnPrimary from "@/components/btn/Primary.vue";
import BtnHero from "@/components/btn/Hero.vue";

const props = defineProps<{
  segment: Segment;
  tagline: string;
  heading: string;
  dark?: boolean;
  description?: string | string[];
  buttonPrimary?: Button;
  buttonSecondary?: Button;
  microcopy?: string;
  disclaimer?: string;
  img?: {
    skewedImg?: boolean;
    rotate?: boolean;
    src: string;
    width: number;
    height: number;
    alt?: string;
    label?: string;
    textSize?: string;
    lemon?: boolean;
    gradientOverlay?: boolean;
    customClass?: string;
  };
}>();

const emit = defineEmits(["click-primary", "click-secondary"]);

interface Button {
  label: string;
  url?: string;
  urlNewWindow?: boolean;
  offSite?: boolean;
}

const isEnterprise = computed(
  () => props.segment === "enterprise" || props.segment === "wholesale",
);

const hasGradient = computed(
  () => props.img && (props.img.gradientOverlay || !!props.img.label),
);
</script>
